import { getAppCapabilities, isAccountOwner } from '@todesktop/shared';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useStore } from '../../store';
import { getInviteLookupEmail } from '../firebase/invitedUsers';
import { onAccountInvitationUpdate } from '../firebase/accountAccess';
import { isAccessDenied } from '../util/accessErrors';

type Permissions = {
  status: 'loading' | 'ready' | 'error';
  canAdminister: boolean;
  canBuild: boolean;
  canRelease: boolean;
  canEdit: boolean;
};
const pending: Permissions = {
  status: 'loading',
  canAdminister: false,
  canBuild: false,
  canRelease: false,
  canEdit: false,
};
const denied: Permissions = {
  status: 'ready',
  canAdminister: false,
  canBuild: false,
  canRelease: false,
  canEdit: false,
};
const ownerPermissions: Permissions = {
  status: 'ready',
  ...getAppCapabilities({ role: 'owner' }),
};
const Context = createContext<Permissions>(pending);
export const useAppPermissions = () => useContext(Context);

export const AppPermissionsProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { ownerId, userId, email } = useStore((state) => ({
    ownerId: state.selectedApp
      ? state.appToUser[state.selectedApp]?.id
      : undefined,
    userId: state.user?.id,
    email: state.user?.email,
  }));
  const key = `${userId}:${ownerId}:${email}`;
  const [result, setResult] = useState<{
    key: string;
    permissions: Permissions;
  }>();
  const isOwner =
    !!userId &&
    ownerId !== undefined &&
    isAccountOwner({ userId }, { accountId: ownerId });

  useEffect(() => {
    if (isOwner || !ownerId || !userId) return;
    let active = true;
    let unsubscribe: (() => void) | undefined;
    const publish = (permissions: Permissions) => {
      if (active) setResult({ key, permissions });
    };
    const fail = (error: unknown) =>
      publish(isAccessDenied(error) ? denied : { ...denied, status: 'error' });
    void getInviteLookupEmail(email || '')
      .then((lookupEmail) => {
        if (!active) return;
        if (!lookupEmail) {
          publish(denied);
          return;
        }
        unsubscribe = onAccountInvitationUpdate(
          ownerId,
          lookupEmail,
          (invite, snapshot) => {
            if (!invite && snapshot?.metadata.fromCache !== false) return;
            publish({
              status: 'ready',
              ...getAppCapabilities({
                role: 'delegate',
                permissions: invite?.permissions,
              }),
            });
          },
          fail,
        );
      })
      .catch(fail);
    return () => {
      active = false;
      unsubscribe?.();
    };
  }, [key, isOwner, ownerId, userId, email]);

  let permissions = result?.key === key ? result.permissions : pending;
  if (isOwner) permissions = ownerPermissions;
  return <Context.Provider value={permissions}>{children}</Context.Provider>;
};
